import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useRef,
} from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HttpError } from '../core/http';
import { authService, LoginRequest, RegisterRequest, User } from '../services';
import {
    registerSessionExpiredHandler,
    SessionExpiredReason,
} from '../utils/authSessionHandler';
import { secureStorage } from '../utils/secureStorage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isEmailVerified: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_EMAIL_VERIFIED'; payload: boolean };

interface LogoutOptions {
  showSuccessToast?: boolean;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  updateUser: (user: User) => void;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isEmailVerified: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isEmailVerified: action.payload.user.is_verified,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isEmailVerified: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isEmailVerified: false,
        isLoading: false,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
        isEmailVerified: action.payload.is_verified,
      };
    case 'SET_EMAIL_VERIFIED':
      return { ...state, isEmailVerified: action.payload };
    default:
      return state;
  }
}

function isHttpError(error: unknown): error is HttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as HttpError).message === 'string'
  );
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAuthenticatedRef = useRef(state.isAuthenticated);
  isAuthenticatedRef.current = state.isAuthenticated;

  const clearSession = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch {
      // Limpa storage local mesmo se o backend falhar.
    }
    dispatch({ type: 'LOGOUT' });
  }, []);

  const handleSessionExpired = useCallback(
    async (_reason: SessionExpiredReason) => {
      if (!isAuthenticatedRef.current && !secureStorage.getItem('access_token')) {
        return;
      }
      await clearSession();
      toast.error(t('auth.sessionExpired'));
      navigate('/login', { replace: true, state: { sessionExpired: true } });
    },
    [clearSession, navigate, t]
  );

  useEffect(() => {
    registerSessionExpiredHandler(handleSessionExpired);
    return () => registerSessionExpiredHandler(null);
  }, [handleSessionExpired]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const token = authService.getAccessToken();
          if (token) {
            if (authService.isTokenNearExpiry()) {
              try {
                const refreshResponse = await authService.refreshToken();
                if (refreshResponse.access_token) {
                  secureStorage.setItem(
                    'access_token',
                    refreshResponse.access_token
                  );
                  secureStorage.setItem(
                    'token_type',
                    refreshResponse.token_type
                  );
                  secureStorage.setItem(
                    'expires_in',
                    refreshResponse.expires_in.toString()
                  );
                  secureStorage.setItem(
                    'token_created_at',
                    Date.now().toString()
                  );
                  if (refreshResponse.refresh_token) {
                    secureStorage.setItem(
                      'refresh_token',
                      refreshResponse.refresh_token
                    );
                  }
                }
              } catch (refreshError) {
                if (
                  isHttpError(refreshError) &&
                  refreshError.status === 401
                ) {
                  await handleSessionExpired('token_expired');
                  return;
                }
              }
            }

            try {
              const user = await authService.getCurrentUser();
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user } });
            } catch (error) {
              if (isHttpError(error) && error.status === 401) {
                await handleSessionExpired('unauthorized');
                return;
              }
              await clearSession();
            }
          }
        } else if (secureStorage.getItem('access_token')) {
          await handleSessionExpired('token_expired');
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    void initializeAuth();
  }, [clearSession, handleSessionExpired]);

  const login = async (credentials: LoginRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      await authService.login(credentials);
      const user = await authService.getCurrentUser();
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user },
      });
      toast.success(t('auth.loginSuccess'));
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      if (isHttpError(error) && error.status === 401) {
        toast.error(t('auth.invalidCredentials'));
      } else {
        const errorMessage = isHttpError(error)
          ? error.message
          : t('auth.loginError');
        toast.error(errorMessage);
      }
      throw error;
    }
  };

  const register = async (userData: RegisterRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      await authService.register(userData);
      await login({
        email: userData.email,
        password: userData.password,
      });
      toast.success(
        'Conta criada com sucesso! Verifique seu email para ativar a conta.'
      );
    } catch (error: unknown) {
      dispatch({ type: 'LOGIN_FAILURE' });

      let errorMessage = 'Erro ao criar conta';

      if (
        typeof error === 'object' &&
        error !== null &&
        'data' in error
      ) {
        const errorWithData = error as { data?: { detail?: string; message?: string } };
        if (errorWithData.data?.detail) {
          errorMessage = errorWithData.data.detail;
        } else if (errorWithData.data?.message) {
          errorMessage = errorWithData.data.message;
        }
      } else if (isHttpError(error)) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async (options?: LogoutOptions): Promise<void> => {
    const showSuccessToast = options?.showSuccessToast !== false;
    try {
      await authService.logout();
      dispatch({ type: 'LOGOUT' });
      if (showSuccessToast) {
        toast.success(t('auth.logoutSuccess'));
      }
    } catch {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateUser = (user: User): void => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const requestPasswordReset = async (email: string): Promise<void> => {
    try {
      await authService.requestPasswordReset(email);
      toast.success(
        'Email de recuperação enviado! Verifique sua caixa de entrada.'
      );
    } catch (error: unknown) {
      const errorMessage =
        (typeof error === 'object' &&
          error !== null &&
          'data' in error &&
          ((error as { data?: { detail?: string } }).data?.detail ||
            (error as { data?: { message?: string } }).data?.message)) ||
        (isHttpError(error) ? error.message : null) ||
        'Erro ao solicitar recuperação de senha';
      toast.error(String(errorMessage));
      throw error;
    }
  };

  const confirmPasswordReset = async (
    token: string,
    newPassword: string
  ): Promise<void> => {
    try {
      await authService.confirmPasswordReset(token, newPassword);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao redefinir senha';
      toast.error(errorMessage);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      if (authService.isAuthenticated()) {
        const user = await authService.getCurrentUser();
        dispatch({ type: 'UPDATE_USER', payload: user });
      }
    } catch (error) {
      if (isHttpError(error) && error.status === 401) {
        await handleSessionExpired('unauthorized');
      }
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    requestPasswordReset,
    confirmPasswordReset,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
